<template>
  <div>
    <!-- Loading State -->
    <div v-if="isLoadingQuestions" class="py-8 text-center">
      <ProgressSpinner style="width: 50px; height: 50px" stroke-width="4" />
      <p class="mt-4 text-sm text-gray-600">Chargement des questions...</p>
    </div>

    <!-- Main Form -->
    <div v-else>
      <!-- Success Messages -->
      <Message v-if="statusMessage.message" :severity="statusMessage.level" class="mb-4">
        {{ statusMessage.message }}
      </Message>

      <!-- Information Panel (only for first-time setup) -->
      <div v-if="isFirstTimeSetup" class="p-4 mb-6 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20">
        <div class="flex items-start">
          <div class="mr-3 text-blue-400">ℹ️</div>
          <div class="text-sm text-blue-700 dark:text-blue-300">
            <strong>Pourquoi configurer des questions de sécurité ?</strong>
            <ul class="mt-2 ml-4 space-y-1 list-disc">
              <li>Permet aux administrateurs de vérifier votre identité en cas de problème</li>
              <li>Nécessaire pour la génération de codes d'urgence</li>
              <li>Renforce la sécurité de votre compte</li>
            </ul>
          </div>
        </div>
      </div>

      <Form v-slot="{ meta }" :validation-schema="validationSchema" @submit="handleSubmit">
        <FieldArray v-slot="{ fields, push, remove }" name="questions">
          <div class="space-y-6">
            <div
              v-for="(field, index) in fields" :key="field.key"
              class="p-4 rounded-lg border border-gray-200 dark:border-gray-600">
              <div class="flex justify-between items-center mb-4">
                <h4 class="font-medium text-gray-900 dark:text-gray-100">
                  Question {{ index + 1 }}
                </h4>
                <Button
                  v-if="fields.length > minQuestions" severity="danger" outlined size="small"
                  type="button" @click="remove(index)">
                  Supprimer
                </Button>
              </div>

              <div class="space-y-4">
                <div>
                  <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Question
                  </label>
                  <!-- Predefined only -->
                  <Field
                    v-if="predefinedQuestions.length > 0 && !allowCustomQuestions" 
                    v-slot="{ field, errorMessage, setValue }" :name="`questions[${index}].question`">
                    <Select 
                      :model-value="field.value" 
                      :options="predefinedQuestions"
                      placeholder="Choisir une question prédéfinie"
                      class="w-full" 
                      :invalid="!!errorMessage" 
                      :disabled="isLoadingQuestions"
                      option-label="question" 
                      option-value="question" 
                      @update:model-value="setValue" />
                    <small v-if="errorMessage" class="text-red-500">{{ errorMessage }}</small>
                  </Field>
                  <!-- Predefined + Custom -->
                  <Field
                    v-else-if="predefinedQuestions.length > 0 && allowCustomQuestions" 
                    v-slot="{ field, errorMessage, setValue }" :name="`questions[${index}].question`">
                    <Select 
                      :model-value="field.value" 
                      :options="[
                        ...predefinedQuestions.map((q) => q.question),
                        'Question personnalisée',
                      ]"
                      placeholder="Choisir une question prédéfinie ou personnalisée" 
                      class="w-full" 
                      :invalid="!!errorMessage"
                      :disabled="isLoadingQuestions" 
                      @update:model-value="setValue" />
                    <small v-if="errorMessage" class="text-red-500">{{ errorMessage }}</small>
                  </Field>
                  <!-- Custom only -->
                  <Field
                    v-else-if="allowCustomQuestions" v-slot="{ field, errorMessage }"
                    :name="`questions[${index}].question`">
                    <InputText
                      v-bind="field" placeholder="Saisissez votre question personnalisée" class="w-full"
                      :invalid="!!errorMessage" :disabled="isLoadingQuestions" />
                    <small v-if="errorMessage" class="text-red-500">{{ errorMessage }}</small>
                  </Field>
                  <!-- Fallback -->
                  <Field v-else v-slot="{ field, errorMessage }" :name="`questions[${index}].question`">
                    <InputText
                      v-bind="field" placeholder="Ex: Quel est le nom de votre premier animal de compagnie ?" 
                      class="w-full" :invalid="!!errorMessage" />
                    <small v-if="errorMessage" class="text-red-500">{{ errorMessage }}</small>
                  </Field>
                </div>

                <!-- Custom question input when "Question personnalisée" is selected -->
                <Field v-slot="{ value }" :name="`questions[${index}].question`">
                  <div v-if="value === 'Question personnalisée'">
                    <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Votre question personnalisée
                    </label>
                    <Field v-slot="{ field, errorMessage }" :name="`questions[${index}].customQuestion`">
                      <InputText
                        v-bind="field" placeholder="Saisissez votre question personnalisée"
                        class="w-full" :invalid="!!errorMessage" />
                      <small v-if="errorMessage" class="text-red-500">{{ errorMessage }}</small>
                    </Field>
                  </div>
                </Field>

                <div>
                  <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Votre réponse
                  </label>
                  <Field v-slot="{ field, errorMessage }" :name="`questions[${index}].answer`">
                    <InputText
                      v-bind="field" 
                      :placeholder="isFirstTimeSetup ? 
                        'Votre réponse (elle sera hachée et stockée de manière sécurisée)' : 
                        'Votre réponse (sensible à la casse)'"
                      class="w-full" :invalid="!!errorMessage" />
                    <small v-if="errorMessage" class="text-red-500">{{ errorMessage }}</small>
                  </Field>
                  <small class="text-gray-500">
                    {{ isFirstTimeSetup ? 
                      'Choisissez une réponse que vous vous rappellerez facilement' : 
                      'La réponse sera stockée de manière sécurisée' }}
                  </small>
                </div>
              </div>
            </div>

            <!-- Add Question Button -->
            <Button
              v-if="fields.length < maxQuestions" 
              severity="secondary" 
              outlined type="button" class="w-full" :disabled="isLoadingQuestions" @click="push({ question: '', answer: '', customQuestion: '' })">
              ➕ Ajouter une question ({{ fields.length }}/{{ maxQuestions }})
            </Button>
          </div>
        </FieldArray>

        <!-- Form Actions -->
        <div class="flex gap-4 mt-6" :class="isFirstTimeSetup ? 'justify-between' : ''">
          <!-- Skip button (only for first-time setup) -->
          <div v-if="isFirstTimeSetup" class="flex flex-col">
            <Button severity="secondary" outlined type="button" :disabled="isSaving" @click="handleSkip">
              ⏭️ Ignorer pour l'instant
            </Button>
            <small class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Vous pourrez configurer vos questions de sécurité plus tard dans votre profil
            </small>
          </div>

          <!-- Submit button -->
          <Button type="submit" severity="primary" :loading="isSaving" :disabled="!meta.valid || isLoadingQuestions">
            {{ isFirstTimeSetup ? '💾 Configurer la sécurité' : '💾 Enregistrer' }}
          </Button>

          <!-- Cancel button (only for profile editing) -->
          <Button v-if="!isFirstTimeSetup" severity="secondary" outlined type="button" @click="handleCancel">
            Annuler
          </Button>
        </div>
      </Form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Form, Field, FieldArray, useForm } from 'vee-validate';
import { 
  securityQuestionsValidationSchema, 
  type SecurityQuestionsForm 
} from '~/types/SecurityQuestionsValidation';

// Props
interface Props {
  isFirstTimeSetup?: boolean;
  existingQuestions?: Array<{question: string, id?: number}>;
}

const props = withDefaults(defineProps<Props>(), {
  isFirstTimeSetup: false,
  existingQuestions: () => []
});

// Emits
const emit = defineEmits<{
  submit: [formData: SecurityQuestionsForm]
  skip: []
  cancel: []
}>();

// Composables
const apiFetch = useSanctumClient();
const { 
  allowCustomQuestions, 
  predefinedQuestions, 
  minQuestions, 
  maxQuestions, 
  loadPredefinedQuestions 
} = useSecurityQuestions();

// Reactive data
const statusMessage = ref({ level: "", message: "" });
const isSaving = ref(false);
const isLoadingQuestions = ref(false);

// VeeValidate setup
const validationSchema = securityQuestionsValidationSchema;
const form = useForm<SecurityQuestionsForm>({
  validationSchema,
  initialValues: {
    questions: props.existingQuestions.length > 0 
      ? props.existingQuestions.map(q => ({ 
          question: q.question, 
          answer: '', 
          customQuestion: '' 
        }))
      : [{ question: "", answer: "", customQuestion: "" }]
  }
});

const { resetForm } = form;

// Methods
async function loadQuestionsAndSettings() {
  try {
    isLoadingQuestions.value = true;
    statusMessage.value = { level: "", message: "" };

    const result = await loadPredefinedQuestions();

    if (result.questions.length === 0 && !result.allowCustom) {
      statusMessage.value = {
        level: "warn",
        message: "Aucune question prédéfinie disponible. Contactez l'administrateur.",
      };
    }
  } catch (error: any) {
    console.error("Error loading predefined questions:", error);
    statusMessage.value = {
      level: "error",
      message: "Erreur lors du chargement des questions. Veuillez rafraîchir la page.",
    };
  } finally {
    isLoadingQuestions.value = false;
  }
}

function handleSubmit(formData: SecurityQuestionsForm) {
  emit('submit', formData);
}

function handleSkip() {
  emit('skip');
}

function handleCancel() {
  emit('cancel');
}

// Update form when existing questions change
watch(() => props.existingQuestions, async (newQuestions) => {
  await nextTick();
  if (newQuestions && newQuestions.length > 0) {
    const questions = newQuestions.map(q => {
      // Check if the existing question matches any predefined question
      const matchesPredefined = predefinedQuestions.value.some(pq => pq.question === q.question);
      
      if (matchesPredefined) {
        return { 
          question: q.question, 
          answer: '', 
          customQuestion: '' 
        };
      } else {
        // If it doesn't match, treat as custom question
        return { 
          question: 'Question personnalisée', 
          answer: '', 
          customQuestion: q.question 
        };
      }
    });
    resetForm({ values: { questions } });
  } else if (!props.isFirstTimeSetup) {
    // For profile editing with no existing questions, start with one empty question
    resetForm({ values: { questions: [{ question: "", answer: "", customQuestion: "" }] } });
  }
}, { deep: true, immediate: true });

// Also watch for predefined questions changes to update existing questions mapping
watch(() => predefinedQuestions.value, () => {
  if (props.existingQuestions && props.existingQuestions.length > 0) {
    const questions = props.existingQuestions.map(q => {
      // Check if the existing question matches any predefined question
      const matchesPredefined = predefinedQuestions.value.some(pq => pq.question === q.question);
      
      if (matchesPredefined) {
        return { 
          question: q.question, 
          answer: '', 
          customQuestion: '' 
        };
      } else {
        // If it doesn't match, treat as custom question
        return { 
          question: 'Question personnalisée', 
          answer: '', 
          customQuestion: q.question 
        };
      }
    });
    resetForm({ values: { questions } });
  }
}, { deep: true });

// Expose methods for parent components
defineExpose({
  setSaving: (saving: boolean) => { isSaving.value = saving; },
  setStatusMessage: (message: {level: string, message: string}) => { statusMessage.value = message; }
});

// Lifecycle
onMounted(() => {
  loadQuestionsAndSettings();
});
</script>